import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Check } from "lucide-react";
import { SectionLabel } from "./Shared";
import { IMAGES } from "../data/siteData";

// A concrete-look SVG texture (concrete substrate — Layer 0)
const ConcreteSlab = ({ style, thicknessLabel }) => (
  <motion.div
    style={style}
    className="absolute inset-0 flex items-center justify-center pointer-events-none"
  >
    <div className="relative w-[85%] max-w-[720px]" style={{ transformStyle: "preserve-3d" }}>
      {/* Top surface */}
      <div
        className="relative aspect-[16/10] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.75)]"
        style={{
          background:
            "linear-gradient(135deg, #6b6b6b 0%, #4a4a4a 45%, #545454 55%, #6f6f6f 100%)",
          transformStyle: "preserve-3d",
        }}
      >
        <svg viewBox="0 0 800 500" className="absolute inset-0 w-full h-full opacity-40">
          <defs>
            <filter id="cnoise">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
              <feColorMatrix values="0 0 0 0 0.3  0 0 0 0 0.3  0 0 0 0 0.3  0 0 0 0.6 0" />
            </filter>
          </defs>
          <rect width="800" height="500" filter="url(#cnoise)" />
          <path d="M50,180 L120,240 L200,220 L280,300" stroke="#2a2a2a" strokeWidth="2" fill="none" />
          <path d="M420,80 L500,180 L620,220 L720,190" stroke="#2a2a2a" strokeWidth="1.5" fill="none" />
          <path d="M180,400 L260,380 L340,420 L450,440" stroke="#2a2a2a" strokeWidth="1.5" fill="none" />
        </svg>
        <div className="absolute top-[30%] left-[20%] w-24 h-16 rounded-full bg-black/40 blur-2xl" />
        <div className="absolute bottom-[15%] right-[25%] w-32 h-20 rounded-full bg-black/50 blur-2xl" />
        <span className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold z-10">Bare Concrete</span>
      </div>
      {/* Front-facing side wall (thickness) */}
      <div
        className="absolute left-0 right-0 h-[36px] bottom-[-36px]"
        style={{
          background: "linear-gradient(180deg, #3d3d3d 0%, #202020 100%)",
          transform: "rotateX(-90deg) translateY(18px)",
          transformOrigin: "top",
        }}
      />
      {/* Thickness label */}
      {thicknessLabel && (
        <div className="absolute -right-3 top-1/2 -translate-y-1/2 translate-x-full pl-6 text-[10px] uppercase tracking-widest text-gray-400 font-semibold whitespace-nowrap">
          <span className="text-[#028090]">▐</span> ~4"–6" slab
        </div>
      )}
    </div>
  </motion.div>
);

// Layer 2 — base coat + full flake broadcast (Dakota Grey style pattern)
const FlakeSlab = ({ style, thicknessLabel }) => {
  const dots = [];
  let seed = 42;
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const chips = ["#3a3f45", "#c8ccd0", "#6a6f75", "#1d2126", "#8b9098"];
  for (let i = 0; i < 260; i++) {
    dots.push({
      left: rand() * 100,
      top: rand() * 100,
      w: 3 + rand() * 7,
      h: 3 + rand() * 6,
      color: chips[i % chips.length],
      rot: rand() * 180,
    });
  }
  return (
    <motion.div style={style} className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="relative w-[85%] max-w-[720px]" style={{ transformStyle: "preserve-3d" }}>
        <div
          className="relative aspect-[16/10] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.75)] overflow-hidden"
          style={{ backgroundColor: "#7a7f85", transformStyle: "preserve-3d" }}
        >
          {dots.map((d, i) => (
            <span
              key={i}
              className="absolute"
              style={{
                left: `${d.left}%`,
                top: `${d.top}%`,
                width: d.w,
                height: d.h,
                backgroundColor: d.color,
                transform: `rotate(${d.rot}deg)`,
              }}
            />
          ))}
          <span className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.2em] text-white/85 font-bold z-10">Base Coat + Full Flake</span>
        </div>
        {/* Thickness edge (shows flake cross-section) */}
        <div
          className="absolute left-0 right-0 h-[22px] bottom-[-22px] overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #6a6f75 0%, #4a4f55 100%)",
            transform: "rotateX(-90deg) translateY(11px)",
            transformOrigin: "top",
          }}
        >
          {/* flakes visible in the side edge */}
          {chips.map((c, i) => (
            <span key={i} className="absolute" style={{ left: `${(i * 17) % 100}%`, top: `${(i * 31) % 80}%`, width: 4, height: 3, backgroundColor: c }} />
          ))}
        </div>
        {thicknessLabel && (
          <div className="absolute -right-3 top-1/2 -translate-y-1/2 translate-x-full pl-6 text-[10px] uppercase tracking-widest text-gray-400 font-semibold whitespace-nowrap">
            <span className="text-[#028090]">▐</span> ~15 mil + full flake
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Layer 3 — glossy UV-stable polyaspartic topcoat
const TopcoatSlab = ({ style, thicknessLabel }) => (
  <motion.div style={style} className="absolute inset-0 flex items-center justify-center pointer-events-none">
    <div className="relative w-[85%] max-w-[720px]" style={{ transformStyle: "preserve-3d" }}>
      <div
        className="relative aspect-[16/10] shadow-[0_50px_100px_-10px_rgba(2,128,144,0.4)] overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(2,128,144,0.08) 40%, rgba(255,255,255,0.05) 100%)",
          backdropFilter: "blur(1px)",
          border: "1px solid rgba(255,255,255,0.2)",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.35) 55%, transparent 70%)",
            mixBlendMode: "overlay",
          }}
        />
        <span className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.2em] text-white font-bold z-10 drop-shadow-lg">UV-Stable Topcoat</span>
      </div>
      {/* Translucent edge */}
      <div
        className="absolute left-0 right-0 h-[14px] bottom-[-14px]"
        style={{
          background: "linear-gradient(180deg, rgba(2,128,144,0.5) 0%, rgba(2,128,144,0.25) 100%)",
          transform: "rotateX(-90deg) translateY(7px)",
          transformOrigin: "top",
          boxShadow: "inset 0 -2px 6px rgba(255,255,255,0.3)",
        }}
      />
      {thicknessLabel && (
        <div className="absolute -right-3 top-1/2 -translate-y-1/2 translate-x-full pl-6 text-[10px] uppercase tracking-widest text-gray-400 font-semibold whitespace-nowrap">
          <span className="text-[#028090]">▐</span> ~5–7 mil polyaspartic
        </div>
      )}
    </div>
  </motion.div>
);

const STEPS = [
  {
    tag: "Layer 1",
    title: "Mechanical Surface Prep",
    desc: "Planetary diamond grinders open the pores of the concrete for a permanent mechanical bond — no acid etching, no shortcuts. This is where every failed coating starts, and where ours starts too — done right.",
    bullets: ["Diamond-ground to structural concrete", "Cracks repaired & ground flush", "HEPA dust capture — no mess"],
  },
  {
    tag: "Layer 2",
    title: "Base Coat + Flake Broadcast",
    desc: "A moisture-tolerant polyaspartic base coat is rolled wet, then your chosen flake blend is broadcast to full coverage — adding strength, style, and slip resistance in a single step.",
    bullets: ["100% flake coverage — no thin spots", "Hides cracks & imperfections", "Choose from 7 premium blends"],
  },
  {
    tag: "Layer 3",
    title: "UV-Stable Polyaspartic Topcoat",
    desc: "A crystal-clear 100% UV-stable aliphatic polyaspartic topcoat locks it all in. Never yellows, never chalks, resists heat and moisture — engineered for the Arizona sun.",
    bullets: ["100% UV stable — never yellows", "Drive on in 24 hours", "Lifetime limited warranty"],
  },
];

export default function CoatingStack3D() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Smooth the scroll for buttery motion
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 32, mass: 0.4 });

  // Scene tilt — subtle rotate as you scroll
  const sceneRotateX = useTransform(smooth, [0, 1], [15, 40]);
  const sceneRotateZ = useTransform(smooth, [0, 1], [-2, 2]);
  const sceneScale = useTransform(smooth, [0, 0.5, 1], [0.95, 1, 1.02]);

  // Layer 1: concrete — always visible, slight lift as we move on
  const concreteY = useTransform(smooth, [0, 1], [0, -60]);
  const concreteOpacity = useTransform(smooth, [0, 0.85, 1], [1, 1, 0.7]);

  // Layer 2: flake — appears around 0.2 → 0.45, translates up and stacks
  const flakeY = useTransform(smooth, [0, 0.2, 0.5, 1], [300, 300, -30, -90]);
  const flakeOpacity = useTransform(smooth, [0.18, 0.32, 1], [0, 1, 1]);
  const flakeTranslateZ = useTransform(smooth, [0, 1], [0, 40]);

  // Layer 3: topcoat — appears around 0.55 → 0.85, stacks on top
  const topY = useTransform(smooth, [0, 0.55, 0.85, 1], [300, 300, -60, -120]);
  const topOpacity = useTransform(smooth, [0.55, 0.72, 1], [0, 1, 1]);
  const topTranslateZ = useTransform(smooth, [0, 1], [0, 80]);

  // Which step is active for the side copy (0, 1, or 2)
  const activeStep = useTransform(smooth, [0, 0.33, 0.66, 1], [0, 0, 1, 2]);

  // Per-step opacity / y — must be declared at top level (no hooks in loops)
  const step1Opacity = useTransform(smooth, [0, 0.02, 0.28, 0.35], [1, 1, 1, 0]);
  const step1Y       = useTransform(smooth, [0, 0.02, 0.28, 0.35], [0, 0, 0, -30]);
  const step2Opacity = useTransform(smooth, [0.28, 0.36, 0.62, 0.7], [0, 1, 1, 0]);
  const step2Y       = useTransform(smooth, [0.28, 0.36, 0.62, 0.7], [30, 0, 0, -30]);
  const step3Opacity = useTransform(smooth, [0.62, 0.7, 1, 1], [0, 1, 1, 1]);
  const step3Y       = useTransform(smooth, [0.62, 0.7, 1, 1], [30, 0, 0, 0]);
  const stepStyles = [
    { opacity: step1Opacity, y: step1Y },
    { opacity: step2Opacity, y: step2Y },
    { opacity: step3Opacity, y: step3Y },
  ];

  // Dot indicator per-step opacity + width
  const dot1Opacity = useTransform(smooth, [0, 0.02, 0.33, 0.36], [1, 1, 1, 0.25]);
  const dot1Width   = useTransform(smooth, [0, 0.02, 0.33, 0.36], [48, 48, 48, 24]);
  const dot2Opacity = useTransform(smooth, [0.33, 0.36, 0.66, 0.7], [0.25, 1, 1, 0.25]);
  const dot2Width   = useTransform(smooth, [0.33, 0.36, 0.66, 0.7], [24, 48, 48, 24]);
  const dot3Opacity = useTransform(smooth, [0.66, 0.7, 1, 1], [0.25, 1, 1, 1]);
  const dot3Width   = useTransform(smooth, [0.66, 0.7, 1, 1], [24, 48, 48, 48]);
  const dotStyles = [
    { opacity: dot1Opacity, width: dot1Width },
    { opacity: dot2Opacity, width: dot2Width },
    { opacity: dot3Opacity, width: dot3Width },
  ];

  const hintOpacity = useTransform(smooth, [0, 0.15], [1, 0]);

  return (
    <section
      ref={containerRef}
      className="relative bg-[#000000] grain motion-safe:h-[280vh]"
      data-testid="coating-stack-3d-section"
      aria-label="Interactive TRI-BOND™ coating system"
    >
      {/* Ambient teal glow */}
      <div
        className="absolute inset-x-0 top-0 bottom-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 60% 40%, rgba(2,128,144,0.18) 0%, transparent 60%)",
        }}
      />

      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-center">
          {/* LEFT: text that swaps between the 3 steps */}
          <div className="relative">
            <SectionLabel>Interactive · Scroll to Build</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-2">
              Watch the <span className="text-[#028090]">TRI-BOND™</span>
              <br />
              System Come to Life
            </h2>
            <p className="text-gray-400 text-sm md:text-base mt-4 mb-8">Three engineered layers. One day of install. A lifetime of floor.</p>

            {/* Stepped copy — cross-fade with scroll progress */}
            <div className="relative h-[320px]">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.tag}
                  style={stepStyles[i]}
                  className="absolute inset-0"
                  data-testid={`stack-step-${i + 1}`}
                >
                  <p className="text-[#E8B86D] text-xs uppercase tracking-[0.25em] font-bold mb-3">{s.tag}</p>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">{s.title}</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">{s.desc}</p>
                  <ul className="space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-gray-300 text-sm">
                        <Check size={16} className="text-[#028090] mt-0.5 shrink-0" strokeWidth={2} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Step indicator dots */}
            <div className="flex gap-2 mt-8" aria-hidden="true">
              {STEPS.map((_, i) => (
                <motion.div
                  key={i}
                  style={dotStyles[i]}
                  className="h-1 bg-[#028090]"
                />
              ))}
            </div>
          </div>

          {/* RIGHT: 3D stacked scene */}
          <motion.div
            className="relative aspect-[4/3] w-full"
            style={{
              perspective: 1400,
              perspectiveOrigin: "50% 40%",
            }}
            data-testid="coating-stack-scene"
          >
            <motion.div
              className="absolute inset-0"
              style={{
                transformStyle: "preserve-3d",
                rotateX: sceneRotateX,
                rotateZ: sceneRotateZ,
                scale: sceneScale,
              }}
            >
              <ConcreteSlab
                style={{ translateZ: 0, y: concreteY, opacity: concreteOpacity }}
                thicknessLabel
              />
              <FlakeSlab
                style={{ translateZ: flakeTranslateZ, y: flakeY, opacity: flakeOpacity }}
                thicknessLabel
              />
              <TopcoatSlab
                style={{ translateZ: topTranslateZ, y: topY, opacity: topOpacity }}
                thicknessLabel
              />
            </motion.div>

            {/* Floor shadow beneath scene */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[70%] h-6 bg-black/60 blur-2xl rounded-full pointer-events-none" />
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.p
          style={{ opacity: hintOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 text-xs uppercase tracking-[0.3em] font-semibold pointer-events-none"
        >
          ↓ Scroll to build ↓
        </motion.p>
      </div>

      {/* Reduced-motion fallback: show 3 static slabs stacked */}
      <div className="motion-safe:hidden motion-reduce:block px-6 md:px-12 py-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.tag} className="bg-white/5 border border-white/10 p-8">
              <img src={i === 0 ? IMAGES.concrete1 : i === 1 ? IMAGES.garage2 : IMAGES.garage3} alt="" loading="lazy" className="w-full aspect-video object-cover mb-5" />
              <p className="text-[#E8B86D] text-xs uppercase tracking-[0.2em] font-bold mb-2">{s.tag}</p>
              <h3 className="font-display text-xl font-bold text-white mb-3">{s.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
